import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy } from 'react';
import { EnvironmentProvider } from '@/lib/environment-context';
import { AuthProvider } from '@/lib/auth-context';
import { AppLayout } from '@/components/layout/AppLayout';

// Lazy load all pages for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard/index'));
const RunTest = lazy(() => import('./pages/RunTest'));
const Info = lazy(() => import('./pages/Info'));
const NotFound = lazy(() => import('./pages/NotFound'));
const TestHistoryAzure = lazy(() => import('./pages/TestHistoryAzure'));
const TestDetailNewAzure = lazy(() => import('./pages/TestDetailNewAzure'));
const ConsistencyAnalysis = lazy(() => import('./pages/ConsistencyAnalysis'));
const SearchAgentOutputs = lazy(() => import('./pages/SearchAgentOutputs'));
const Comparison = lazy(() => import('./pages/Comparison'));
const ComparisonResults = lazy(() => import('./pages/ComparisonResults'));
const UserLookup = lazy(() => import('./pages/UserLookup/index'));
const UserDetail = lazy(() => import('./pages/UserLookup/UserDetail'));
const ProductInsights = lazy(() => import('./pages/ProductInsights/index'));
const ProductInsightsDebug = lazy(() => import('./pages/ProductInsights/DebugView'));

// V2 Batch Testing pages
const TestRunDetailV2 = lazy(() => import('./pages/TestRunDetailV2'));
const QuestionEvalDetailV2 = lazy(() => import('./pages/QuestionEvalDetailV2'));
const MetricsHistory = lazy(() => import('./pages/MetricsHistory'));

// Report Wizards
const ReportWizards = lazy(() => import('./pages/ReportWizards'));
const ReportWizardDetail = lazy(() => import('./pages/ReportWizardDetail'));

// Credit Analytics
const CreditAnalytics = lazy(() => import('./pages/CreditAnalytics'));
const CreditAnalyticsUserDetail = lazy(() => import('./pages/CreditAnalyticsUserDetail'));

// Benchmarks (unified tabs: Question Lists + Test Runs)
const Benchmarks = lazy(() => import('./pages/Benchmarks'));

// Mock page for local testing (no real session data needed)
const MockSessionDetail = lazy(() => import('./pages/MockSessionDetail'));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <AuthProvider>
        <EnvironmentProvider>
          <BrowserRouter>
          <Routes>
            {/* All routes wrapped in persistent AppLayout - sidebar stays mounted during navigation */}
            <Route element={<AppLayout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Navigate to="/" replace />} />
              <Route path="/chat-judge" element={<RunTest />} />
              <Route path="/session-history" element={<TestHistoryAzure />} />
              <Route path="/session/:sessionId" element={<TestDetailNewAzure />} />
              <Route path="/report-wizards" element={<ReportWizards />} />
              <Route path="/report-wizards/:id" element={<ReportWizardDetail />} />
              {/* Benchmarks (unified tabs) */}
              <Route path="/benchmarks" element={<Benchmarks />} />
              {/* V2 Batch Testing routes (legacy, redirect to unified Benchmarks page) */}
              <Route path="/question-lists" element={<Navigate to="/benchmarks" replace />} />
              <Route path="/test-runs" element={<Navigate to="/benchmarks?tab=runs" replace />} />
              <Route path="/test-runs/:id" element={<TestRunDetailV2 />} />
              <Route path="/test-runs/:testRunId/evaluation/:evalId" element={<QuestionEvalDetailV2 />} />
              <Route path="/metrics-history" element={<MetricsHistory />} />
              <Route path="/performance" element={<MetricsHistory focusSection="performance" />} />
              <Route path="/consistency-analysis" element={<ConsistencyAnalysis />} />
              <Route path="/search-agent-outputs" element={<SearchAgentOutputs />} />
              <Route path="/comparison" element={<Comparison />} />
              <Route path="/comparison/results" element={<ComparisonResults />} />
              <Route path="/credit-analytics" element={<CreditAnalytics />} />
              <Route path="/credit-analytics/:userId" element={<CreditAnalyticsUserDetail />} />
              <Route path="/product-insights" element={<ProductInsights />} />
              <Route path="/product-insights/debug" element={<ProductInsightsDebug />} />
              {/* Legacy routes for backward compatibility */}
              <Route path="/search-guide-comparison" element={<Comparison />} />
              <Route path="/search-guide-comparison/results" element={<ComparisonResults />} />
              <Route path="/user-lookup" element={<UserLookup />} />
              <Route path="/user-lookup/:userId" element={<UserDetail />} />
              <Route path="/info" element={<Info />} />
              {/* Legacy route aliases for compatibility */}
              <Route path="/run-test" element={<RunTest />} />
              <Route path="/session-history-azure" element={<TestHistoryAzure />} />
              <Route path="/session-azure/:sessionId" element={<TestDetailNewAzure />} />
              {/* Mock session for local testing */}
              <Route path="/mock-session" element={<MockSessionDetail />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </EnvironmentProvider>
    </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;

